import React from 'react';
import axios from 'axios';
import useAsyncEffect from 'use-async-effect/';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme, Theme } from '@mui/material/styles';

import { configureAuth } from '@core/auth';
import PolicyModal from '@core/myta/components/PolicyModal';
import LogoutNotificationModal from '@components/LogoutNotificationModal';
import NotificationProvider from '@providers/NotificationProvider';
import { MytaverseProvider } from '@providers/MytaverseProvider';
import { NavigationHistoryProvider } from './providers/NavigationHistoryProvider';
import Routes from './routes/Routes';
import i18next from './locales/i18n';
import { env } from 'env';
import {
  setSentryFrontendVersion,
  setSentryApiVersion,
} from './helpers/sentry';

import './styles/App.scss';

const {
  // @ts-ignore
  REACT_APP_CLIENT_ENV,
  // @ts-ignore
  REACT_APP_CLIENT_DEFAULT_FONTS,
  // @ts-ignore
  REACT_APP_HUB_VERSION,
  // @ts-ignore
  REACT_APP_API_URL,
} = env;

console.log(`Hub version v${REACT_APP_HUB_VERSION}`);

setSentryFrontendVersion(REACT_APP_HUB_VERSION);

if (REACT_APP_CLIENT_DEFAULT_FONTS.includes('Noto Sans')) {
  require('./styles/fonts/noto.scss');
}
if (REACT_APP_CLIENT_DEFAULT_FONTS.includes('Poppins')) {
  require('./styles/fonts/poppins.scss');
}

const theme: Theme = createTheme({
  typography: {
    fontFamily: REACT_APP_CLIENT_DEFAULT_FONTS,
  },
});

switch (REACT_APP_CLIENT_ENV) {
  case 'EK':
    require('./styles/Ek.scss');
    break;
  default:
    require('./styles/Default.scss');
}

configureAuth();

declare module 'axios' {
  export interface AxiosRequestConfig {
    sendAuth0Headers?: boolean;
  }
}

const App = () => {
  useAsyncEffect(async () => {
    const {
      data: { version },
    } = await axios.get(`${REACT_APP_API_URL}api/version`);

    console.log(`API version: v${version}`);

    setSentryApiVersion(version);
  }, []);

  return (
    <I18nextProvider i18n={i18next}>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <CssBaseline />
          <NavigationHistoryProvider>
            <NotificationProvider>
              <MytaverseProvider>
                <Routes />
                <PolicyModal />
                <LogoutNotificationModal />
              </MytaverseProvider>
            </NotificationProvider>
          </NavigationHistoryProvider>
        </BrowserRouter>
      </ThemeProvider>
    </I18nextProvider>
  );
};

export default App;
